"use client";

import { logger as mainLogger } from "@klarify/telemetry";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { beforeUnloadManager } from "./beforeunload";

export interface ProtectOptions {
	/**
	 * Whether client-side navigation should also be held back. Defaults to true
	 * for unsaved-work callers, where leaving the route loses the work.
	 *
	 * A live recording is the exception: it outlives the route because the
	 * upload manager sits outside React, so it blocks reloads (which would kill
	 * the worker) while leaving navigation free.
	 */
	blockNavigation?: boolean;
}

export interface ReloadProtectionState {
	reload: () => () => void;
	protect: (options?: ProtectOptions) => () => void;
	isProtected: boolean;
	blocksNavigation: boolean;
	willReload: boolean;
}

const ReloadProtectionContext = createContext<ReloadProtectionState | null>(
	null,
);

const logger = mainLogger.meta({ source: "Reload Protection" }).seal();

export function ReloadProtectionProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const [reloadQueue, setReloadQueue] = useState(0);
	const [semaphore, setSemaphore] = useState(0);
	const [navSemaphore, setNavSemaphore] = useState(0);

	useEffect(() => {
		// Gated on navigation-blocking protection, not reload protection: the
		// generic copy only fits callers whose work dies with the route. A
		// recording holds reload protection without it and registers its own,
		// more accurate warning — this listener would otherwise pre-empt it,
		// since the first listener to return a message wins.
		if (navSemaphore > 0) {
			const unsub = beforeUnloadManager.addCancellableListener(() => {
				return "Leaving this page will lose unsaved changes! Are you sure you want to leave?";
			});
			return () => {
				unsub();
			};
		}
	}, [navSemaphore]);

	useEffect(() => {
		if (semaphore === 0 && reloadQueue > 0) {
			logger.info("Reloading page...");
			setReloadQueue(0);
			window.location.reload();
		}
	}, [semaphore, reloadQueue]);

	useEffect(() => {
		logger.debug(
			{
				reloadQueue,
				semaphore,
			},
			"Reload protection state updated",
		);
	}, [reloadQueue, semaphore]);

	const isProtected = useMemo(() => {
		return semaphore > 0;
	}, [semaphore]);

	const blocksNavigation = useMemo(() => {
		return navSemaphore > 0;
	}, [navSemaphore]);

	const willReload = useMemo(() => {
		return reloadQueue > 0;
	}, [reloadQueue]);

	const protect = useCallback((options?: ProtectOptions) => {
		const blockNavigation = options?.blockNavigation ?? true;
		logger.info({ blockNavigation }, "Protecting reload...");
		setSemaphore((s) => s + 1);
		if (blockNavigation) {
			setNavSemaphore((s) => s + 1);
		}
		// A boolean guard, not a mutated `value`: React state updaters run at
		// render time, so mutating a captured `value` to 0 before the updater
		// runs makes `s - value` a no-op and the semaphore never releases —
		// which pins navigation blocked until a full reload.
		let released = false;
		return () => {
			if (released) {
				return;
			}
			released = true;
			logger.info("Releasing reload protection...");
			setSemaphore((s) => s - 1);
			if (blockNavigation) {
				setNavSemaphore((s) => s - 1);
			}
		};
	}, []);

	const reload = useCallback(() => {
		logger.info("Queueing reload...");
		let dequeued = false;
		setReloadQueue((q) => q + 1);
		return () => {
			if (dequeued) {
				return;
			}
			dequeued = true;
			logger.info("Dequeueing reload...");
			setReloadQueue((q) => q - 1);
		};
	}, []);

	return (
		<ReloadProtectionContext.Provider
			value={{ protect, reload, isProtected, blocksNavigation, willReload }}
		>
			{children}
		</ReloadProtectionContext.Provider>
	);
}

export function useReloadProtection() {
	const context = useContext(ReloadProtectionContext);
	if (context === null) {
		throw new Error(
			"useReloadProtection must be used within a ReloadProtectionProvider",
		);
	}
	return context;
}

export function useReloadProtectionOptional() {
	return useContext(ReloadProtectionContext);
}
